import type { ChamadoGestao } from "../../../types/portalCliente";

/** Dias civis decorridos: 23h50 para 00h10 é "ontem", não "há 0 dias". */
export const diasDesde = (iso?: string | null, agora = new Date()): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dia = 24 * 60 * 60 * 1000;
  const de = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const ate = Date.UTC(agora.getFullYear(), agora.getMonth(), agora.getDate());
  return Math.max(0, Math.round((ate - de) / dia));
};

const DIAS_POR_MES = 30.44;

export const textoDeDias = (dias: number): string => {
  if (dias <= 0) return "hoje";
  if (dias === 1) return "ontem";
  if (dias < 31) return `há ${dias} dias`;
  if (dias < 365) {
    const meses = Math.max(1, Math.round(dias / DIAS_POR_MES));
    return `há ${meses} ${meses === 1 ? "mês" : "meses"}`;
  }
  const anos = Math.floor(dias / 365);
  return `há ${anos} ${anos === 1 ? "ano" : "anos"}`;
};

/**
 * Aguardando Cliente é o único estado em que a bola está com quem lê o portal.
 * O texto do status vem livre do banco, então a checagem é por padrão.
 */
export const aguardandoCliente = (status?: string | null): boolean =>
  /aguardando\s*cliente/i.test(status ?? "");

/** Há quantos dias o chamado espera resposta do cliente; nulo se não espera. */
export const diasDeEspera = (c: ChamadoGestao, agora = new Date()): number | null => {
  if (!aguardandoCliente(c.statusChamado)) return null;
  // A espera começa na última mexida no chamado, não na abertura.
  return diasDesde(c.dataModificacao ?? c.dataCriacao, agora);
};

export interface ResumoAba {
  total: number;
  aguardandoVoce: number;
  esperaMaisLongaDias: number | null;
  /** Quantidade por severidade, de 0 Crítica a 3 Baixa. */
  porSeveridade: [number, number, number, number];
}

/**
 * Resumo dos itens carregados. Os contadores das abas dão o total; "aguardando
 * você" só existe olhando item a item.
 */
export const resumirAba = (itens: ChamadoGestao[], agora = new Date()): ResumoAba => {
  let aguardandoVoce = 0;
  let esperaMaisLongaDias: number | null = null;
  const porSeveridade: [number, number, number, number] = [0, 0, 0, 0];

  for (const c of itens) {
    const indice = c.severidade == null ? 3 : Math.min(3, Math.max(0, c.severidade));
    porSeveridade[indice]++;

    const espera = diasDeEspera(c, agora);
    if (espera !== null) {
      aguardandoVoce++;
      if (esperaMaisLongaDias === null || espera > esperaMaisLongaDias) {
        esperaMaisLongaDias = espera;
      }
    }
  }

  return { total: itens.length, aguardandoVoce, esperaMaisLongaDias, porSeveridade };
};

export const DIAS_RESOLVIDOS = 90;

export interface ResumoResolvidos {
  resolvidos: number;
  /** Mediana, e não média: um chamado parado meses distorceria a média. */
  medianaDias: number | null;
}

/**
 * O que foi entregue na janela. A lista não traz data de conclusão garantida;
 * para um finalizado, a última modificação é, na prática, o fechamento. É uma
 * aproximação, e está assim na origem também.
 */
export const resumirResolvidos = (
  finalizados: ChamadoGestao[],
  agora = new Date(),
  dias = DIAS_RESOLVIDOS
): ResumoResolvidos => {
  const duracoes: number[] = [];

  for (const c of finalizados) {
    const fechamento = c.dataModificacao ?? c.dataCriacao;
    const haQuanto = diasDesde(fechamento, agora);
    if (haQuanto === null || haQuanto > dias) continue;
    duracoes.push(diasDesde(c.dataCriacao, new Date(fechamento)) ?? 0);
  }

  if (duracoes.length === 0) return { resolvidos: 0, medianaDias: null };

  duracoes.sort((a, b) => a - b);
  const meio = Math.floor(duracoes.length / 2);
  const mediana =
    duracoes.length % 2 ? duracoes[meio] : (duracoes[meio - 1] + duracoes[meio]) / 2;

  return { resolvidos: duracoes.length, medianaDias: mediana };
};

/** Acima disso o chamado está parado: o mesmo corte que a origem usa. */
export const DIAS_PARADO = 90;

/**
 * Acima deste tamanho o grupo vira parede: dezenas de cards de mesma
 * severidade empurram para fora da tela o que exige ação.
 */
export const LIMITE_DO_GRUPO = 8;

export interface RecorteGrupo {
  mostrados: ChamadoGestao[];
  aguardando: number;
  parados: number;
}

/**
 * Recorte de um grupo grande: primeiro o que espera o cliente, depois o que
 * envelheceu, e o resto completa até o limite na ordem já priorizada. Os
 * prioritários nunca ficam de fora, mesmo passando do limite — são eles que
 * justificam o recorte.
 */
export const recortarGrupo = (
  lista: ChamadoGestao[],
  agora = new Date()
): RecorteGrupo => {
  const aguardando = lista.filter((c) => diasDeEspera(c, agora) !== null);
  const parados = lista.filter(
    (c) =>
      !aguardando.includes(c) && (diasDesde(c.dataCriacao, agora) ?? 0) >= DIAS_PARADO
  );
  const prioritarios = [...aguardando, ...parados];
  const resto = lista.filter((c) => !prioritarios.includes(c));

  return {
    mostrados: [...prioritarios, ...resto].slice(
      0,
      Math.max(LIMITE_DO_GRUPO, prioritarios.length)
    ),
    aguardando: aguardando.length,
    parados: parados.length,
  };
};

/**
 * Valor que se repete em mais da metade da lista. Carimbo igual em toda linha
 * não diferencia nada: ele sai das linhas e vira nota no cabeçalho, e o que
 * fica na linha é a exceção — que é onde está a informação.
 */
export const valorDominante = (valores: (string | null | undefined)[]): string | null => {
  const presentes = valores.filter((v): v is string => Boolean(v));
  if (presentes.length < 4) return null;

  const contagem = new Map<string, number>();
  for (const v of presentes) contagem.set(v, (contagem.get(v) ?? 0) + 1);

  const [valor, quantidade] = [...contagem.entries()].sort((a, b) => b[1] - a[1])[0];
  return quantidade > presentes.length / 2 ? valor : null;
};

/**
 * Descrição que só repete o assunto não acrescenta nada — e acontece bastante:
 * quem abre o chamado costuma colar o mesmo texto nos dois campos.
 */
export const descricaoUtil = (c: ChamadoGestao): string | null => {
  const descricao = c.descricao?.trim();
  if (!descricao) return null;
  return descricao.toLowerCase() === c.assunto.trim().toLowerCase() ? null : descricao;
};
